import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { 
  History, 
  Loader2, 
  User,
  Calendar,
  ArrowRight,
  FileText,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const ACTION_LABELS = {
  create: { label: 'Aangemaakt', color: 'bg-emerald-100 text-emerald-700' },
  update: { label: 'Gewijzigd', color: 'bg-amber-100 text-amber-700' },
  delete: { label: 'Verwijderd', color: 'bg-red-100 text-red-700' },
  complete: { label: 'Voltooid', color: 'bg-blue-100 text-blue-700' }
};

const FIELD_LABELS = {
  name: 'Naam',
  title: 'Titel',
  description: 'Beschrijving',
  status: 'Status',
  severity: 'Ernst',
  criticality: 'Kritiekheid',
  probability: 'Waarschijnlijkheid',
  impact: 'Impact',
  owner: 'Eigenaar',
  domain: 'Domein',
  deadline: 'Deadline',
  goal: 'Doel',
  asset_type: 'Asset type',
  location: 'Locatie',
  tags: 'Tags'
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nee';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-';
  return String(value);
};

const AuditLogPanel = ({ 
  entityType, 
  entityId = null, 
  buttonVariant = 'ghost',
  buttonSize = 'sm',
  showLabel = true,
  className = ''
}) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const endpoint = entityId 
        ? `/ggrc/audit-logs/${entityType}/${entityId}`
        : `/ggrc/audit-logs/${entityType}`;
      const res = await api.get(endpoint);
      setLogs(res.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, entityType, entityId]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMM yyyy HH:mm', { locale: nl });
    } catch {
      return dateString;
    }
  };

  const getActionBadge = (action) => {
    const config = ACTION_LABELS[action] || { label: action, color: 'bg-neutral-100 text-neutral-700' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-xs text-neutral-400">Geen details beschikbaar</p>;
    }

    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([field, value]) => {
          const fieldLabel = FIELD_LABELS[field] || field;
          const oldValue = value?.old;
          const newValue = value?.new;

          // Skip if both values are the same
          if (oldValue === newValue) return null;

          return (
            <div key={field} className="text-xs">
              <span className="font-medium text-neutral-600">{fieldLabel}:</span>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-neutral-400 line-through">{formatValue(oldValue)}</span>
                <ArrowRight className="h-3 w-3 text-neutral-400" />
                <span className="text-neutral-900 dark:text-white">{formatValue(newValue)}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          className={className}
          data-testid={`audit-log-button-${entityType}`}
        >
          <History className="h-4 w-4" strokeWidth={1.5} />
          {showLabel && <span className="ml-2">Historie</span>}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" strokeWidth={1.5} />
            Wijzigingsgeschiedenis
          </SheetTitle>
          <SheetDescription>
            {entityId 
              ? 'Alle wijzigingen voor dit item'
              : `Recente wijzigingen in ${entityType}`
            }
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-4 pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-neutral-500">Geen wijzigingen gevonden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div key={log.id || index} className="relative">
                  {index < logs.length - 1 && (
                    <div className="absolute left-4 top-10 bottom-0 w-px bg-neutral-200 dark:bg-neutral-700" />
                  )}
                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 z-10">
                      <User className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-black dark:text-white">
                          {log.user_name || 'Systeem'}
                        </span>
                        {getActionBadge(log.action)}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
                        <Calendar className="h-3 w-3" strokeWidth={1.5} />
                        {formatDate(log.timestamp)}
                      </div>
                      
                      {log.reason && (
                        <div className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded text-xs">
                          <span className="font-medium">Reden: </span>
                          {log.reason}
                        </div>
                      )}
                      
                      <div className="mt-2 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                        {renderChanges(log.changes)}
                      </div>
                    </div>
                  </div>
                  {index < logs.length - 1 && <Separator className="mt-4 bg-transparent" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default AuditLogPanel;
